/* eslint-disable no-nested-ternary */
import { Tooltip } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import { ReactComponent as Clock } from '@assets/svg/icons/logbook/OutlinedClock.svg';
import Device from '@assets/svg/icons/logbook/Device';
import VehicleIcon from '@assets/svg/icons/logbook/Vehicle';
import { IDrivesTable } from '@common/interfaces/LogbookAPI';
import { ArrowBlock } from './components/Pagination';
import { Icons } from './icons';
import { ClientSignature, TripIdRow } from './components';

function MaxLines(value: string, lines = 3) {
  return <p className={`max-lines-${lines}`}>{value}</p>;
}

function Agency(agency: string, size = 33) {
  const Logo = Icons.icon(agency);
  return <Logo fill="#E0E8ED" width={size} height={size} />;
}

function Distance(distance: string) {
  const parsedDistance: { distanceNovacom?: string; distance?: string } = JSON.parse(distance);
  return (
    <div>
      {MaxLines(
        parsedDistance.distanceNovacom
          ? `${parsedDistance.distanceNovacom} km`
          : parsedDistance.distance
          ? `${parsedDistance.distance} km`
          : `-`,
        2,
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {!!parsedDistance?.distanceNovacom && (
          <div style={{ marginRight: 8 }}>
            <Tooltip title="Vehicle GPS">
              <VehicleIcon fill="#EAF2F9" />
            </Tooltip>
          </div>
        )}
        {!!parsedDistance?.distance && (
          <Tooltip title="Mobile phone GPS">
            <Device fill="#EAF2F9" />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function Vehicle(vehicle: string) {
  // eslint-disable-next-line react/destructuring-assignment
  const vehicleInfo = vehicle.split('|');
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginTop: 4 }}>{Agency(vehicleInfo[0], 20)}</div>
        <p
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            marginLeft: 4,
          }}
        >
          {vehicleInfo[2]}
        </p>
      </span>
      <p>{vehicleInfo[1]}</p>
    </div>
  );
}

function PickUpTime(time: string): JSX.Element {
  return (
    <span style={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
      <Clock />
      <span style={{ marginLeft: 8 }}>{time}</span>
    </span>
  );
}

function GoToArrow(): JSX.Element {
  return <ArrowBlock type="next" style={{ borderRadius: 4 }} onClick={() => {}} />;
}

export const DoneColumns = [
  {
    title: 'trip id',
    key: 'id',
    render: (trip: IDrivesTable) => ({
      props: {
        style: { background: '#262829' },
      },
      children: <TripIdRow id={trip.id} signatureCode={trip.signatureCode} />,
    }),
  },
  {
    title: 'Drive Time',
    dataIndex: 'driveTime',
    key: 'driveTime',
    sortDirections: ['descend', 'ascend'] as SortOrder[],
    render: (v: string): JSX.Element => MaxLines(v, 2),
    sorter: true,
  },
  { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: Vehicle, width: 120 },
  {
    title: 'driver',
    dataIndex: 'driverName',
    key: 'driverName',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax',
    dataIndex: 'paxName',
    key: 'paxName',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  { title: 'Pax n°', dataIndex: 'paxN', key: 'passengers_number', width: 90 },
  { title: 'agency', dataIndex: 'paxAgency', key: 'paxAgency', width: 90 },
  {
    title: 'Pick-up',
    dataIndex: 'pickUp',
    key: 'pickUp',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  {
    title: 'drop-off',
    dataIndex: 'dropOff',
    key: 'dropOff',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  { title: 'Km', dataIndex: 'km', key: 'km', render: Distance, width: 90 },
  {
    title: 'Duration',
    dataIndex: 'time',
    key: 'time',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  { title: 'type', dataIndex: 'transferType', key: 'transferType' },
  {
    title: 'SIGNATURE',
    dataIndex: 'clientSignature',
    key: 'clientSignature',
    render: ClientSignature,
  },
  { title: '', key: 'goTo', render: GoToArrow, width: 70 },
];

export const SmallDoneColumns = [
  {
    title: 'trip id',
    key: 'id',
    render: (trip: IDrivesTable) => ({
      props: {
        style: { background: '#262829' },
      },
      children: <TripIdRow id={trip.id} signatureCode={trip.signatureCode} />,
    }),
  },
  {
    title: 'Drive Time',
    dataIndex: 'driveTime',
    key: 'driveTime',
    width: 120,
    sortDirections: ['descend', 'ascend'] as SortOrder[],
    render: (v: string): JSX.Element => MaxLines(v, 2),
    sorter: true,
  },
  { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: Vehicle, width: 120 },
  {
    title: 'driver',
    dataIndex: 'driverName',
    key: 'driverName',
    width: 120,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax',
    dataIndex: 'paxName',
    key: 'paxName',
    width: 120,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  { title: 'Pax n°', dataIndex: 'paxN', key: 'passengers_number', width: 90 },
  { title: 'agency', dataIndex: 'paxAgency', key: 'paxAgency', width: 90 },
  {
    title: 'Pick-up',
    dataIndex: 'pickUp',
    key: 'pickUp',
    width: 120,
    render: (v: string): JSX.Element => MaxLines(v),
  },
  {
    title: 'drop-off',
    dataIndex: 'dropOff',
    key: 'dropOff',
    width: 120,
    render: (v: string): JSX.Element => MaxLines(v),
  },
  { title: 'Km', dataIndex: 'km', key: 'km', render: Distance, width: 90 },
  {
    title: 'Duration',
    dataIndex: 'time',
    key: 'time',
    width: 90,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'SIGNATURE',
    dataIndex: 'clientSignature',
    key: 'clientSignature',
    render: ClientSignature,
  },
];

export const SmallToDoColumns = [
  {
    title: 'trip id',
    key: 'id',
    render: (trip: IDrivesTable) => ({
      props: {
        style: { background: '#262829' },
      },
      children: <TripIdRow id={trip.id} signatureCode={trip.signatureCode} />,
    }),
  },
  {
    title: 'Drive Time',
    dataIndex: 'driveTime',
    key: 'driveTime',
    sortDirections: ['descend', 'ascend'] as SortOrder[],
    sorter: true,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: Vehicle, width: 150 },
  {
    title: 'driver',
    dataIndex: 'driverName',
    key: 'driverName',
    width: 150,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax',
    dataIndex: 'paxName',
    key: 'paxName',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax n°',
    dataIndex: 'paxN',
    key: 'passengers_number',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'agency',
    dataIndex: 'paxAgency',
    key: 'paxAgency',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pick-up',
    dataIndex: 'pickUp',
    key: 'pickUp',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  {
    title: 'drop-off',
    dataIndex: 'dropOff',
    key: 'dropOff',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  { title: 'Pick-up time', dataIndex: 'pickUpTime', key: 'pickUpTime', render: PickUpTime },
];

export const ToDoColumns = [
  {
    title: 'trip id',
    key: 'id',
    render: (trip: IDrivesTable) => ({
      props: {
        style: { background: '#262829' },
      },
      children: <TripIdRow id={trip.id} signatureCode={trip.signatureCode} />,
    }),
  },

  {
    title: 'Drive Time',
    dataIndex: 'driveTime',
    key: 'driveTime',
    sortDirections: ['descend', 'ascend'] as SortOrder[],
    sorter: true,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: Vehicle, width: 150 },
  {
    title: 'driver',
    dataIndex: 'driverName',
    key: 'driverName',
    width: 150,
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax',
    dataIndex: 'paxName',
    key: 'paxName',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pax n°',
    dataIndex: 'paxN',
    key: 'passengers_number',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'agency',
    dataIndex: 'paxAgency',
    key: 'paxAgency',
    render: (v: string): JSX.Element => MaxLines(v, 2),
  },
  {
    title: 'Pick-up',
    dataIndex: 'pickUp',
    key: 'pickUp',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  {
    title: 'drop-off',
    dataIndex: 'dropOff',
    key: 'dropOff',
    render: (v: string): JSX.Element => MaxLines(v),
  },
  { title: 'type', dataIndex: 'transferType', key: 'transferType' },
  { title: 'Pick-up time', dataIndex: 'pickUpTime', key: 'pickUpTime', render: PickUpTime },
  { title: '', key: 'goTo', render: GoToArrow, width: 70 },
];

export function tableColumns(toDo: boolean, isSmallScreen: boolean) {
  if (isSmallScreen) {
    return toDo ? SmallToDoColumns : SmallDoneColumns;
  }
  return toDo ? ToDoColumns : DoneColumns;
}
